import { ILoginWidgetData, ILoginWidgetDataShort, TranslateService } from "@pageBuilder/typings";
import { LoginScriptRenderer } from "./login-script-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";
import { DomUtil } from "src/utils/dom-util";
import { MembersAreaWidgetRendererBase } from "../common/members-area-widget-renderer-base";

export class LoginWidgetRenderer extends MembersAreaWidgetRendererBase<ILoginWidgetData> {

    protected override readonly DATA_ATTRIBUTE = "data-login";
    private readonly scriptRenderer: LoginScriptRenderer;
    private readonly translate: any;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new LoginScriptRenderer();

        this.translate = TranslateService.translator(renderingCommonContext.data.lang);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("loginWidget");
    }

    protected override renderInternal(element: HTMLElement, data: ILoginWidgetData): HTMLElement {
        this.setAttributes(element, data);
        this.renderContent(element, data);
        this.renderStyles(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        return element;
    }

    private renderContent(element: HTMLElement, data: ILoginWidgetData): void {
        const content = element.querySelector<HTMLElement>(".widget-content");
        if (!content) {
            return;
        }

        DomUtil.deleteAllChildElements(content);

        this.renderTitle(content);
        this.renderLoginForm(content, data);
        this.renderRegisterText(content);
    }

    private renderTitle(container: HTMLElement): void {
        const title = document.createElement("span");
        title.classList.add("title");
        title.textContent = this.translate.LOGIN.TITLE;
        title.title = title.textContent ?? "";

        container.appendChild(title);
    }

    private renderLoginForm(container: HTMLElement, data: ILoginWidgetData): void {
        const form = document.createElement("form");
        form.classList.add("widget-form");

        const emailFieldData = data.fields?.find(x => x.type === "Email");
        const passwordFieldData = data.fields?.find(x => x.type === "Password");

        const labelsStyle = data.style.labels?.style ?? "standard";

        this.renderControl(form, emailFieldData, {
            inputName: "login-email",
            inputType: "email",
            inputMaxLength: this.EMAIL_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "off" : "email",
            labelText: this.translate.LOGIN.EMAIL,
            labelStyle: labelsStyle,
        });

        this.renderControl(form, passwordFieldData, {
            inputName: "login-password",
            inputType: "password",
            inputMaxLength: this.PASSWORD_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "new-password" : "current-password",
            labelText: this.translate.LOGIN.PASSWORD,
            labelStyle: labelsStyle
        });

        this.renderForgotPasswordLink(form);
        this.renderSubmitButton(form, data.style.submit?.theme ?? "primary", this.translate.LOGIN.BUTTON_TEXT);

        container.appendChild(form);
    }

    private renderForgotPasswordLink(container: HTMLElement): void {
        const wrapper = document.createElement("div");
        wrapper.classList.add("forgot-password-link-wrapper");

        const anchor = document.createElement("a");
        // href will be added at runtime
        anchor.classList.add("forgot-password-link");
        anchor.innerText = this.translate.LOGIN.FORGOT_YOUR_PASSWORD;

        wrapper.appendChild(anchor);
        container.appendChild(wrapper);
    }

    private renderRegisterText(container: HTMLElement): void {
        const anchor = document.createElement("a");
        // href will be added at runtime
        anchor.classList.add("register-link");
        anchor.textContent = this.translate.LOGIN.SIGN_UP;

        const nonBreakingSpaceElement = document.createTextNode("\u00A0");

        const registerTextElement = document.createElement("span");
        registerTextElement.classList.add("register-text");
        registerTextElement.textContent = this.translate.LOGIN.SIGN_UP_QUESTION;

        registerTextElement.append(nonBreakingSpaceElement, anchor);

        container.appendChild(registerTextElement);
    }

    protected override prepareDataForSave(data: ILoginWidgetData): ILoginWidgetDataShort {
        return {
            fields: data.fields,
            submit: data.submit
        };
    }

    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`];
    }

}
